import React from 'react';
import { observer } from 'mobx-react-lite';
import { UseFormReset } from 'react-hook-form';

import { Controller, Form } from '@trader/components';
import { useI18next } from '@trader/services';
import { Login } from '@trader/containers';
import { images } from '@trader/assets';
import { useMst } from '@trader/store';

import { schema } from './schema';

import * as Styled from './styled';

type TInitialState = {
  username: string;
  password: string;
};

type THandleSubmit = (
  reset: UseFormReset<TInitialState>,
  data: TInitialState,
  getRecaptchaBeforeSubmit: () => Promise<string>
) => void;

const defaultValues = {
  username: '',
  password: '',
};

export const SignIn: React.FC = observer(() => {
  const { translate } = useI18next();
  const store = useMst();

  const handleSubmit: THandleSubmit = async (
    reset,
    data,
    getRecaptchaBeforeSubmit
  ) => {
    const token = await getRecaptchaBeforeSubmit();
    if (token) {
      await store.auth.loginAsync.run({ ...data, captcha: token });
      reset(defaultValues);
    }
  };

  return (
    <Login
      logoIconType={import.meta.env.VITE_LOGO}
      bgImage={images.loginBackground}
    >
      {getRecaptchaBeforeSubmit => (
        <Form<TInitialState>
          onSubmit={(reset, data) =>
            handleSubmit(reset, data, getRecaptchaBeforeSubmit)
          }
          defaultValues={defaultValues}
          validationSchema={schema}
        >
          {({ register, control }) => (
            <>
              <Controller
                type='textField'
                onTranslateErrors={translate}
                fullWidth
                autoFocus
                label={translate('COMMON.LABELS.EMAIL')}
                control={control}
                {...register('username')}
              />
              <Controller
                type='passwordField'
                onTranslateErrors={translate}
                fullWidth
                label={translate('COMMON.LABELS.PASSWORD')}
                control={control}
                {...register('password')}
              />
              <Styled.ForgotPassword
                onClick={() => {
                  window.open(import.meta.env.VITE_FORGOT_PASSWORD_LINK);
                }}
              >
                {translate('SIGN_IN.FORGOT_PASSWORD')}
              </Styled.ForgotPassword>
              <Styled.Submit
                loading={store.auth.loginAsync.inProgress}
                disabled={store.auth.loginAsync.inProgress}
                fullWidth
                variant='text'
                type='submit'
              >
                {translate('SIGN_IN.TITLE')}
              </Styled.Submit>
            </>
          )}
        </Form>
      )}
    </Login>
  );
});
